import { Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { IAggregatedStats } from '@feathr/blackbox';
import { Skeleton } from '@feathr/components';

import type { TAttributionModel } from '../ReportsConversionsDashboardPage.utils';
import DeltaIndicator from './DeltaIndicator';
import { calcDelta, formatAttrLabel, formatConversionValue, getAttrStats } from './KpiTiles.utils';
import TileError from './TileError';

import * as styles from './KpiTiles.css';

export interface ITotalValueTileProps {
  attr: TAttributionModel;
  currentStats?: IAggregatedStats;
  hasError: boolean;
  isLoading: boolean;
  priorStats?: IAggregatedStats;
}

function TotalValueTile({
  attr,
  currentStats,
  hasError,
  isLoading,
  priorStats,
}: Readonly<ITotalValueTileProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const currentRoi = getAttrStats(currentStats, attr)?.roi ?? 0;
  const priorRoi = getAttrStats(priorStats, attr)?.roi ?? 0;
  const delta = calcDelta(currentRoi, priorRoi);

  if (hasError) {
    return <TileError label={t('Total Conversion Value')} />;
  }

  return (
    <Stack gap={theme.spacing['2']}>
      <span className={styles.label}>{t('Total Conversion Value')}</span>
      {isLoading ? (
        <>
          <Skeleton height={36} width={160} />
          <Skeleton height={16} width={200} />
        </>
      ) : (
        <>
          <span className={styles.primaryStat}>{formatConversionValue(currentRoi)}</span>
          <DeltaIndicator
            delta={delta}
            subtext={
              <>
                <br />
                {t('Attributed via {{model}}', { model: formatAttrLabel(attr, t) })}
              </>
            }
          />
        </>
      )}
    </Stack>
  );
}

export default TotalValueTile;
